import { addToRecord, updateRecord } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { GroupSidebarLoadFulfilledAction } from '../../domains/groupSidebarLoad/actions.types';
import {
  EditPinnedLinkTitleFulfilledAction,
  EditPinnedLinkTitlePendingAction,
  PinObjectFulfilledAction,
  PinObjectRejectedAction,
  UnpinObjectFulfilledAction,
} from '../../domains/pinnedResources/actions.types';
import { pendingPinnedObjectEdgeId } from '../../domains/pinnedResources/types';
import { Group } from '../types';

import { GroupPinnedResources } from './types';

export type GroupPinnedResourceAction =
  | EditPinnedLinkTitleFulfilledAction
  | EditPinnedLinkTitlePendingAction
  | GroupSidebarLoadFulfilledAction
  | PinObjectFulfilledAction
  | PinObjectRejectedAction
  | UnpinObjectFulfilledAction;

const initialState: GroupPinnedResources = {};

const stateWithFulfilledGroupPinnedResources = (
  state: GroupPinnedResources,
  { id, entities }: GroupSidebarLoadFulfilledAction['payload']
): GroupPinnedResources => addToRecord(state, id, entities.groupPinnedResources[id]);

const removePinnedObjectFromPinnedResourcesState = (
  state: GroupPinnedResources,
  { groupId, unpinnedObjectEdgeId }: UnpinObjectFulfilledAction['payload']
): GroupPinnedResources =>
  updateRecord(state, groupId, {
    id: groupId,
    pinnedResources: state[groupId].pinnedResources.filter((resource) => resource.edgeId !== unpinnedObjectEdgeId),
  });

const updatePendingPinnedObjectEdgeId = (
  state: GroupPinnedResources,
  { groupId, edgeId, pinnedObjectId }: PinObjectFulfilledAction['payload']
): GroupPinnedResources => {
  const newPinnedResources = state[groupId].pinnedResources.map((resource) => {
    if (resource.edgeId === pendingPinnedObjectEdgeId && resource.node.id === pinnedObjectId) {
      return {
        ...resource,
        edgeId,
      };
    }

    return resource;
  });

  return updateRecord(state, groupId, {
    id: groupId,
    pinnedResources: newPinnedResources,
  });
};

const removePendingPinnedObjectsFromPinnedResourcesState = (
  state: GroupPinnedResources,
  groupId: Group['id']
): GroupPinnedResources =>
  updateRecord(state, groupId, {
    id: groupId,
    pinnedResources: state[groupId].pinnedResources.filter((resource) => resource.edgeId !== pendingPinnedObjectEdgeId),
  });

const addPendingPinnedObjectToPinnedResourcesState = (
  state: GroupPinnedResources,
  { groupId, result: node, pinnedLinkOverrideTitle }: EditPinnedLinkTitleFulfilledAction['payload']
): GroupPinnedResources => {
  const pinnedResources = [
    ...state[groupId].pinnedResources,
    { edgeId: pendingPinnedObjectEdgeId, node, pinnedLinkTitle: pinnedLinkOverrideTitle },
  ];

  return updateRecord(state, groupId, {
    id: groupId,
    pinnedResources,
  });
};

const reducer: Reducer<GroupPinnedResources, GroupPinnedResourceAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_SIDEBAR.LOAD_FULFILLED':
      return stateWithFulfilledGroupPinnedResources(state, action.payload);
    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_FULFILLED':
      return addPendingPinnedObjectToPinnedResourcesState(state, action.payload);
    case 'GROUP_PINNED_RESOURCES.EDIT_LINK_TITLE_PENDING':
      return removePendingPinnedObjectsFromPinnedResourcesState(state, action.payload.groupId);
    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_FULFILLED':
      return updatePendingPinnedObjectEdgeId(state, action.payload);
    case 'GROUP_PINNED_RESOURCES.PIN_OBJECT_REJECTED':
      return removePendingPinnedObjectsFromPinnedResourcesState(state, action.payload.groupId);
    case 'GROUP_PINNED_RESOURCES.UNPIN_OBJECT_FULFILLED':
      return removePinnedObjectFromPinnedResourcesState(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
